/**
 * Open a record where you are, from any screen that holds its id (G12).
 *
 * SaleFish could already do this for a contact — the global search opens the
 * contact modal over whatever page you are on — but only from AppShell's own
 * state, so nothing else could ask for it: a sale row, a notification, a
 * worksheet naming its suite, a just-created contact. This is the one door:
 *
 *   openRecord({ type: "unit", id, projectId })
 *
 * and `<RecordDialogHost />` (mounted once, in AppShell) opens the matching
 * dialog over the current page: the contact modal, the unit (suite) dialog,
 * the offer (worksheet) sheet, or a quote summary. Nothing navigates, so the
 * list you were reading is still there when the dialog closes.
 *
 * A window event rather than a React context on purpose: the callers live on
 * both sides of AppShell's providers (pages, widgets, lazily loaded dialogs,
 * the service-worker notification handler), and an event reaches all of them
 * without threading a provider through each.
 *
 * `openRecord` returns false when no host is mounted — the caller can fall
 * back to navigating (or to doing nothing, as create-then-continue does).
 */

export type RecordRef =
  | {
      type: "contact";
      id: string;
      projectId?: string | null;
      /** A contact-detail tab or filter alias, as ContactDetailModal's `initialTab`. */
      tab?: string;
      fileId?: string;
      /** The record itself when the caller already has it (saves a request). */
      record?: unknown;
    }
  | { type: "unit"; id: string; projectId: string; record?: unknown }
  | { type: "offer"; id: string; projectId: string; record?: unknown }
  | { type: "sale"; id: string; projectId: string }
  | { type: "quote"; id: string; record?: unknown };

export type RecordType = RecordRef["type"];

export const OPEN_RECORD_EVENT = "salefish:open-record";

let hosts = 0;

/** Called by RecordDialogHost on mount; returns the unregister function. */
export function registerRecordHost(): () => void {
  hosts += 1;
  return () => {
    hosts = Math.max(0, hosts - 1);
  };
}

export function isRecordHostMounted(): boolean {
  return hosts > 0;
}

export function openRecord(ref: RecordRef): boolean {
  if (typeof window === "undefined" || !isRecordHostMounted()) return false;
  window.dispatchEvent(new CustomEvent<RecordRef>(OPEN_RECORD_EVENT, { detail: ref }));
  return true;
}
